import { useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { routeAllowed, useGateContext } from "./routeGating";
import { useTranslation } from "react-i18next";
import { LivePage } from "../pages/LivePage";
import { GettingStartedPage } from "../pages/GettingStartedPage";
import { HomePage } from "../pages/HomePage";
import { FleetPage } from "../pages/FleetPage";
import { AlertsPage } from "../pages/AlertsPage";
import { NetworkPage } from "../pages/NetworkPage";
import { InventoryPage } from "../pages/InventoryPage";
import { InventoryRunPage } from "../pages/InventoryRunPage";
import { InventoryKitsPage } from "../pages/InventoryKitsPage";
import { AssetsAdminPage } from "../pages/AssetsAdminPage";
import { OeePage } from "../pages/OeePage";
import { FleetReportPage } from "../pages/FleetReportPage";
import SafetyPage from "../pages/SafetyPage";
import { WarehouseAdminPage } from "../pages/WarehouseAdminPage";
import { ShiftsAdminPage } from "../pages/ShiftsAdminPage";
import { RatesAdminPage } from "../pages/RatesAdminPage";
import { AuditPage } from "../pages/AuditPage";
import { AssistantGapsPage } from "../pages/AssistantGapsPage";
import { SitesAdminPage } from "../pages/SitesAdminPage";
import { TenantsAdminPage } from "../pages/TenantsAdminPage";
import { AccessAdminPage } from "../pages/AccessAdminPage";
import { DevicesAdminPage } from "../pages/DevicesAdminPage";
import { useUIStore } from "../stores/uiStore";
import { useSiteStore, ROLE_RANK } from "../stores/siteStore";
import { shouldShowSetup } from "./provisioning";
import { useProvisioning } from "../hooks/useProvisioning";
import { warehouseRoutes } from "./warehouseRoutes";
import { useRealtime, useRealtimeStatus } from "./useRealtime";
import { Sidebar } from "./Sidebar";
import { Breadcrumb } from "./Breadcrumb";
import { AssistantPanel } from "../assistant/AssistantPanel";
import { useAssistantStore } from "../assistant/assistantStore";
import { NavIcon } from "./NavIcons";
import lockupOnDark from "../assets/brand/lockup-on-dark.svg";
import { PRODUCT_NAME } from "./brand";
import styles from "./App.module.css";

const LANGS = [
  { code: "pt-BR", label: "PT" },
  { code: "en-US", label: "EN" },
] as const;

/**
 * State-aware landing (ADR 175 + 274): `/` is the overview once the system is
 * operational, and the guided setup before that — an empty dashboard is a worse
 * first screen than the checklist that fills it. The store is populated before
 * first render (siteStore.initFromToken awaits sites + warehouses), so this
 * reads synchronously — no flash.
 *
 * ADR 318: only a *resolved, successful* empty list means "not provisioned yet".
 * An in-flight or failed load reading as zero is how an expired token turned the
 * overview into the setup checklist — the system was fine, the answer never
 * arrived. When in doubt, show the overview: its own widgets report their errors.
 */
function RootRedirect() {
  const hasWarehouse = useSiteStore((s) => s.availableWarehouses.length > 0);
  const resolved = useSiteStore((s) => s.warehousesResolved);
  const failed = useSiteStore((s) => s.warehousesError);
  const { role } = useProvisioning();
  return shouldShowSetup(hasWarehouse, resolved, failed, role)
    ? <Navigate to="/setup" replace />
    : <HomePage />;
}

export function App() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const navCollapsed = useUIStore((s) => s.navCollapsed);
  const setNavCollapsed = useUIStore((s) => s.setNavCollapsed);
  const authed = useSiteStore((s) => !!s.token);
  const sitesResolved = useSiteStore((s) => s.sitesResolved);
  const gateCtx = useGateContext();
  const routeDenied = authed && sitesResolved && !routeAllowed(pathname, gateCtx);
  const auth = (window as unknown as { __auth?: { username?: string; logout?: () => void } }).__auth;
  // ADR 356: the diagnostic reveal is an admin's investigation tool — the
  // toggle exists only for site admins and up. gateVisible's vocabulary, but
  // read directly: this is UI chrome, not a routed surface.
  const canReveal = useSiteStore((s) => s.isGlobalAdmin || s.roleRank >= ROLE_RANK["site-admin"]);
  const revealMode = useUIStore((s) => s.revealMode);
  const toggleRevealMode = useUIStore((s) => s.toggleRevealMode);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const assistantOpen = useAssistantStore((s) => s.open);
  const toggleAssistant = useAssistantStore((s) => s.toggle);
  const activeSiteId = useSiteStore((s) => s.activeSiteId);
  useRealtime();
  const realtimeStatus = useRealtimeStatus();

  // A status light that is only visible when everything is fine tells you
  // nothing. /live always shows it; everywhere else it stays quiet while the
  // socket is up and speaks the moment it drops — /fleet, /alerts and the
  // Network counts all read the same stream, and frozen data with no
  // explanation is the failure this is meant to prevent (ADR 224).
  //
  // `idle` is never shown anywhere: no site in scope means there is nothing to
  // connect to, and a grant-less user reading "DESCONECTADO" would go hunting
  // for a network fault that does not exist (ADR 225).
  const showRealtimeStatus =
    realtimeStatus !== "idle" && (pathname.startsWith("/live") || realtimeStatus !== "online");

  // Auto-collapse the rail on /live so the 3D canvas keeps its width; an
  // explicit user toggle (persisted) wins over the auto rule.
  const collapsed = navCollapsed ?? pathname.startsWith("/live");

  return (
    <div className={styles.shell}>
      <div className={styles.rail}>
        <Sidebar collapsed={collapsed} onToggleCollapsed={() => setNavCollapsed(!collapsed)} />
      </div>
      <header className={styles.topbar}>
        <button
          type="button"
          className={styles.menuBtn}
          onClick={() => setDrawerOpen(true)}
          aria-label={t("a11y.openNav")}
        >
          <NavIcon name="menu" />
        </button>
        {/* ADR 208: the brand lives in the full-width top bar, not the rail, so
            it stays put when the menu collapses/expands. ADR 386: the lockup is one
            outlined SVG (mark + wordmark), so no component spells the name. */}
        <div className={styles.brand}>
          <img src={lockupOnDark} alt={PRODUCT_NAME} className={styles.brandLogo} />
        </div>
        <div className={styles.pickers}>
          <Breadcrumb />
        </div>
        <div className={styles.topbarRight}>
          {authed && !!activeSiteId && (
            <button
              type="button"
              className={assistantOpen ? styles.langActive : styles.langBtn}
              aria-pressed={assistantOpen}
              title={t("assistant.openHint")}
              onClick={toggleAssistant}
            >
              {t("assistant.open")}
            </button>
          )}
          {authed && canReveal && (
            <button
              type="button"
              className={`${revealMode ? styles.langActive : styles.langBtn} ${styles.diagBtn}`}
              aria-pressed={revealMode}
              title={t("diag.toggleHint")}
              onClick={toggleRevealMode}
            >
              {t("diag.toggle")}
            </button>
          )}
          <div className={styles.langToggle} role="group" aria-label={t("a11y.language")}>
            {LANGS.map(({ code, label }) => (
              <button
                key={code}
                className={i18n.language === code ? styles.langActive : styles.langBtn}
                onClick={() => i18n.changeLanguage(code)}
                aria-pressed={i18n.language === code}
              >
                {label}
              </button>
            ))}
          </div>
          {showRealtimeStatus && (
            <span className={styles.live} data-status={realtimeStatus} role="status">
              ● {t(`live.status.${realtimeStatus}`)}
            </span>
          )}
          {authed && (
            <div className={styles.account}>
              {auth?.username && <span className={styles.accountName}>{auth.username}</span>}
              <button type="button" className={styles.logoutBtn} onClick={() => auth?.logout?.()}>
                {t("auth.logout")}
              </button>
            </div>
          )}
        </div>
      </header>
      <main className={styles.main}>
        {/* ADR 395: the menu's gate, applied to the address bar too. Waits for the site list —
            an unresolved scope has no capabilities yet and would bounce a valid deep link. */}
        {routeDenied ? <Navigate to="/" replace /> : <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/setup"   element={<GettingStartedPage />} />
          <Route path="/live"    element={<LivePage />} />
          <Route path="/fleet"   element={<FleetPage />} />
          <Route path="/alerts"  element={<AlertsPage />} />
          <Route path="/oee"     element={<OeePage />} />
          <Route path="/fleet-report" element={<FleetReportPage />} />
          <Route path="/safety"  element={<SafetyPage />} />
          <Route path="/network" element={<NetworkPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/inventory/runs/:runId" element={<InventoryRunPage />} />
          <Route path="/inventory/kits" element={<InventoryKitsPage />} />
          <Route path="/admin/warehouses" element={<WarehouseAdminPage />} />
          <Route path="/admin/shifts" element={<ShiftsAdminPage />} />
          <Route path="/admin/rates" element={<RatesAdminPage />} />
          {/* Mapping is warehouse-anchored (ADR 145); the old standalone URL
              lands on the warehouse list where the per-row link lives. */}
          <Route path="/admin/mapping" element={<Navigate to="/admin/warehouses" replace />} />

          {/* The warehouse page, its tabs and the legacy URLs that redirect onto
              them — mounted from a value so the tests assert THIS definition
              rather than a hand-written replica of it. */}
          {warehouseRoutes}
          <Route path="/admin/assets" element={<AssetsAdminPage />} />
          <Route path="/admin/devices" element={<DevicesAdminPage />} />
          <Route path="/admin/tenants" element={<TenantsAdminPage />} />
          <Route path="/admin/sites"  element={<SitesAdminPage />} />
          <Route path="/admin/audit"  element={<AuditPage />} />
          <Route path="/admin/assistant-gaps" element={<AssistantGapsPage />} />
          <Route path="/admin/access" element={<AccessAdminPage />} />
          {/* Never strand on a blank page: any unknown URL (e.g. a stale
              post-login path) resolves to the state-aware landing (ADR 177). */}
          <Route path="*" element={<RootRedirect />} />
        </Routes>}
      </main>
      {authed && !!activeSiteId && <AssistantPanel />}
      {drawerOpen && (
        <div className={styles.drawerLayer}>
          <div className={styles.backdrop} onClick={() => setDrawerOpen(false)} />
          <div className={styles.drawer}>
            <Sidebar
              collapsed={false}
              onToggleCollapsed={() => setDrawerOpen(false)}
              onNavigate={() => setDrawerOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
